'use client';

import { WebshopProduct } from '@cldsystems/ws-api-client';
import { CSSProperties, FC, Key, useMemo, useState } from 'react';
import ProductCard from '@/components/products/product-card/product-card';
import { Tab, Tabs } from '@/components/ui';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { cn } from '@/utils';
import {
  getContentVariantFromBackground,
  pickReadableTextColor,
} from '@/utils/content-variant-from-background';
import { mapProductToSimpleProductInfo } from '@/utils/map-product-to-simple-product-info';

export type FeaturedProductsSectionItem = {
  title: string;
  products: Array<WebshopProduct | null>;
};

export type FeaturedProductsSectionProps = {
  featuredProducts: FeaturedProductsSectionItem[];
  sectionTitle?: {
    text: string;
    color?: string;
    fontSize?: string;
    fontWeight?: string;
  };
  /** Section background color (e.g. for Studio section styling) */
  background?: string;
  /** Optional tab visual style configured from Studio */
  tabsStyle?: {
    background?: string;
    textColor?: string;
  };
  /** When false, product cards do not navigate (e.g. Studio preview) */
  interactive?: boolean;
  /** With `interactive={false}`, show cart / favorites when general feature settings allow (Studio section preview) */
  showAddToCart?: boolean;
  showFavorites?: boolean;
  /** When true, grid shows "Loading…" instead of "No products" when empty (e.g. Studio preview) */
  productsLoading?: boolean;
  paddingStyle?: CSSProperties;
};

const toTranslucentBackground = (color: string, alpha = 0.2): string => {
  const normalized = color.trim();
  const hex = normalized.replace('#', '');

  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    const r = parseInt(`${hex[0]}${hex[0]}`, 16);
    const g = parseInt(`${hex[1]}${hex[1]}`, 16);
    const b = parseInt(`${hex[2]}${hex[2]}`, 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  return normalized;
};

/** Context-agnostic presentational section: receives full products via props, no fetch, no "Show more". */
const FeaturedProductsSection: FC<FeaturedProductsSectionProps> = ({
  featuredProducts,
  sectionTitle,
  background,
  tabsStyle,
  interactive = true,
  showAddToCart = false,
  showFavorites = false,
  productsLoading = false,
  paddingStyle,
}) => {
  const resolveThemeColor = useResolveThemeColor();
  const resolvedBackground = resolveThemeColor(background);
  const contentVariant = useMemo(
    () => getContentVariantFromBackground(resolvedBackground),
    [resolvedBackground],
  );
  const resolvedTabsBackground = resolveThemeColor(tabsStyle?.background);
  const resolvedTabsListBackground = resolvedTabsBackground
    ? toTranslucentBackground(resolvedTabsBackground)
    : '';
  const resolvedTabsTextColor = resolveThemeColor(tabsStyle?.textColor);
  const tabsInlineStyle: CSSProperties = {};
  if (resolvedTabsBackground) {
    (tabsInlineStyle as Record<string, string>)['--products-tabs-selected-bg'] =
      resolvedTabsBackground;
    (tabsInlineStyle as Record<string, string>)['--products-tabs-list-bg'] =
      resolvedTabsListBackground;
  }
  if (resolvedTabsTextColor) {
    (tabsInlineStyle as Record<string, string>)['--products-tabs-text'] = resolvedTabsTextColor;
  }
  const tabsToShow = featuredProducts.filter(
    section => section.title != null && section.title !== '',
  );
  const [selectedTab, setSelectedTab] = useState<Key>(tabsToShow[0]?.title ?? '');

  const currentProducts =
    featuredProducts
      .find(section => section.title === selectedTab)
      ?.products.filter(
        (product): product is NonNullable<typeof product> =>
          product !== null && product !== undefined,
      ) ?? [];

  const showEmptyState = !interactive && currentProducts.length === 0;
  const emptyMessage = productsLoading ? 'Loading…' : 'No products';

  const resolvedTitleColor = sectionTitle?.color
    ? pickReadableTextColor(
        resolveThemeColor(sectionTitle.color),
        resolvedBackground,
        resolveThemeColor('foreground'),
        resolveThemeColor('content1'),
      )
    : undefined;

  return (
    <div
      className="flex w-full flex-col items-center gap-2"
      style={{
        ...(resolvedBackground ? { backgroundColor: resolvedBackground } : {}),
        ...paddingStyle,
      }}
    >
      {sectionTitle?.text && (
        <h2
          className={cn('text-center', {
            'font-normal': sectionTitle.fontWeight === 'light',
            'font-medium': sectionTitle.fontWeight === 'normal',
            'font-bold':
              sectionTitle.fontWeight === 'bold' || sectionTitle.fontWeight === 'semi-bold',
          })}
          style={{
            color: resolvedTitleColor,
            fontSize: sectionTitle.fontSize,
          }}
        >
          {sectionTitle.text}
        </h2>
      )}
      {tabsToShow.length > 0 && (
        <div className="w-full touch-pan-x scrollbar-thin overflow-x-auto overflow-y-hidden pb-3">
          <div className="flex w-full min-w-fit justify-center">
            <Tabs
              className="w-fit"
              style={tabsInlineStyle}
              classNames={{
                cursor: cn(
                  'w-full',
                  resolvedTabsBackground ? 'bg-[var(--products-tabs-selected-bg)]' : '',
                ),
                tabList: cn(
                  'flex w-fit flex-row gap-8',
                  resolvedTabsBackground ? 'bg-[var(--products-tabs-list-bg)]' : '',
                ),
                tab: cn(
                  'w-fit flex-none whitespace-nowrap px-4',
                  resolvedTabsTextColor
                    ? '!text-[var(--products-tabs-text)] data-[selected=true]:!text-[var(--products-tabs-text)]'
                    : '',
                ),
                tabContent: cn(
                  'w-fit',
                  resolvedTabsTextColor ? '!text-[var(--products-tabs-text)]' : '',
                ),
              }}
              variant="solid"
              radius="full"
              onSelectionChange={value => setSelectedTab(value)}
            >
              {tabsToShow.map(item => (
                <Tab key={item.title} title={item.title} />
              ))}
            </Tabs>
          </div>
        </div>
      )}
      <div className="mt-12 flex w-full flex-wrap justify-center gap-4">
        {currentProducts.map((product, index) => (
          <div
            key={product.id || product.sku || index}
            className="w-[calc(50%-0.5rem)] max-w-[258px] @md:w-[calc((100%-2rem)/3)] @lg:w-[calc((100%-3rem)/4)]"
          >
            <ProductCard
              product={mapProductToSimpleProductInfo(product)}
              interactive={interactive}
              showAddToCart={showAddToCart}
              showFavorites={showFavorites}
              contentVariant={contentVariant}
            />
          </div>
        ))}
        {showEmptyState && (
          <p
            className={cn(
              'w-full text-center',
              contentVariant === 'dark' ? 'text-white/80' : 'text-zinc-600',
            )}
          >
            {emptyMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default FeaturedProductsSection;
